import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentMarkupApi } from '../utils/documentMarkupApi';
import './DocumentMarkupUploader.css';

function DocumentMarkupUploader() {
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const handleFileSelect = (file) => {
        setError('');
        
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a PDF or image (PNG, JPG).');
            return;
        }
        
        // Validate file size (10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File too large. Maximum size is 10MB.');
            return;
        }
        
        setSelectedFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        
        try {
            setUploading(true);
            setError('');
            const result = await documentMarkupApi.uploadDocument(selectedFile);
            navigate(`/my-notebook/document-markup/${result.id}`);
        } catch (err) {
            setError(err.message);
            setUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="DocumentMarkupUploader__container">
            <div className="DocumentMarkupUploader__header">
                <button 
                    className="DocumentMarkupUploader__back-btn"
                    onClick={() => navigate('/my-notebook/document-markup')}
                >
                    <i className="fas fa-arrow-left"></i> Back
                </button>
                <h2>Upload Document</h2>
            </div>

            {error && (
                <div className="DocumentMarkupUploader__error">
                    <i className="fas fa-exclamation-circle"></i> {error}
                </div>
            )}

            <div 
                className={`DocumentMarkupUploader__dropzone ${dragActive ? 'DocumentMarkupUploader__dropzone--active' : ''}`}
                onDrop={handleDrop}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
            >
                <i className="fas fa-cloud-upload-alt"></i>
                <p className="DocumentMarkupUploader__dropzone-text">
                    Drag and drop a file here
                </p>
                <p className="DocumentMarkupUploader__dropzone-or">or</p>
                <label className="DocumentMarkupUploader__file-label">
                    <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                handleFileSelect(e.target.files[0]);
                            }
                        }}
                        className="DocumentMarkupUploader__file-input"
                    />
                    Choose File
                </label>
                <p className="DocumentMarkupUploader__dropzone-hint">
                    PDF, PNG, or JPG • Max 10MB
                </p>
            </div>

            {selectedFile && (
                <div className="DocumentMarkupUploader__preview">
                    <div className="DocumentMarkupUploader__preview-icon">
                        <i className={`fas fa-file-${selectedFile.type === 'application/pdf' ? 'pdf' : 'image'}`}></i>
                    </div>
                    <div className="DocumentMarkupUploader__preview-info">
                        <h3>{selectedFile.name}</h3>
                        <p>{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <button
                        className="DocumentMarkupUploader__preview-remove"
                        onClick={() => setSelectedFile(null)}
                        disabled={uploading}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            )}

            <div className="DocumentMarkupUploader__actions">
                <button
                    className="DocumentMarkupUploader__btn-cancel"
                    onClick={() => navigate('/my-notebook/document-markup')}
                    disabled={uploading}
                >
                    Cancel
                </button>
                <button
                    className="DocumentMarkupUploader__btn-upload"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Uploading...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-upload"></i> Upload & Start Markup
                        </>
                    )}
                </button>
            </div>

            <div className="DocumentMarkupUploader__info">
                <h4>Document Markup Features:</h4>
                <ul>
                    <li>Draw with pen and highlighter</li>
                    <li>Circle and underline important parts</li>
                    <li>Erase annotations</li>
                    <li>Auto-save your markups</li>
                </ul>
            </div>
        </div>
    );
}

export default DocumentMarkupUploader;
